// Component type constants
export const COMPONENT = {
  SPP: "SPP",
  MMI: "MMI",
  IFRAME: "IFRAME",
  SPACER: "SPACER",
  PAGEBREAK: "PAGEBREAK",
  TDX: "TDX",
  CODE: "CODE",
  SOCIAL_EXPLORER_TABLE: "SOCIAL_EXPLORER_TABLE",
  JOURNAL: "JOURNAL",
  CODEGRADE: "CODEGRADE",
  GLOSSARY: "GLOSSARY",
  SINGLESPA: "SINGLESPA",
  ETEXT_MCQ: "ETEXT_MCQ",
} as const;

export const STAGE_ELEMENT = {
  PLACEHOLDER: "stage-element-placeholder",
  TEXT: "stage-element-text",
  IMAGE: "stage-element-image",
  PLUGIN: "stage-element-component-plugin",
  EMBED: "stage-element-embedded-html",
  TABLE: "stage-element-table",
  SHOWHIDE: "stage-element-show-hide",
} as const;

export const DEVICE_TYPE = {
  IOS: "ios",
  ANDROID: "android",
  WEB: "web",
} as const;

/** Union of device platforms: "web" | "ios" | "android" */
export type DeviceType = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE];

/** Returns true for native mobile platforms (ios or android), false for web. */
export function isNativeMobileDeviceType(deviceType: DeviceType): boolean {
  return deviceType === DEVICE_TYPE.IOS || deviceType === DEVICE_TYPE.ANDROID;
}

export const REMOTE_URLS: Record<
  string,
  { cloudflicks: string; TDX: string; WSOL: string; fitjsUrl: string; commonIesClientId: string }
> = {
  dev: {
    cloudflicks: "https://spp-dev.pearson.com/mf/sppPlayerMfRemoteEntry.js",
    TDX: "https://cdn-dev.acs.pearsondev.tech/tdx-fit-provider/dev/remoteEntryNoBundle.js",
    WSOL: "https://wsng-mf-stg.pearson.com/uiNgMfRemoteEntry.js",
    fitjsUrl: "https://learningapps-ui-ppe.pearson.com/b0/stg/fit/v1.4.2/fit.min.js",
    commonIesClientId: "oX5Vm6SEEeSi5QBEAU0081tx20UHE469",
  },
  stg: {
    cloudflicks: "https://spp-stg.pearson.com/mf/sppPlayerMfRemoteEntry.js",
    TDX: "https://cdn-dev.acs.pearsondev.tech/tdx-fit-provider/stg/remoteEntryNoBundle.js",
    WSOL: "https://wsng-mf-stg.pearson.com/uiNgMfRemoteEntry.js",
    fitjsUrl: "https://learningapps-ui-ppe.pearson.com/b0/stg/fit/v1.4.2/fit.min.js",
    commonIesClientId: "oX5Vm6SEEeSi5QBEAU0081tx20UHE469",
  },
  qaint: {
    cloudflicks: "https://spp-int.pearson.com/mf/sppPlayerMfRemoteEntry.js",
    TDX: "https://cdn-dev.acs.pearsondev.tech/tdx-fit-provider/qa/remoteEntryNoBundle.js",
    WSOL: "https://wsng-mf-int.pearson.com/uiNgMfRemoteEntry.js",
    fitjsUrl: "https://learningapps-ui-ppe.pearson.com/b0/qa-int/fit/latest/fit.min.js",
    commonIesClientId: "oX5Vm6SEEeSi5QBEAU0081tx20UHE469",
  },
  ppe: {
    cloudflicks: "https://spp-nft.pearson.com/mf/sppPlayerMfRemoteEntry.js",
    TDX: "https://cdn-dev.acs.pearsondev.tech/tdx-fit-provider/ppe/remoteEntryNoBundle.js",
    WSOL: "https://wsng-mf-nft.pearson.com/uiNgMfRemoteEntry.js",
    fitjsUrl: "https://learningapps-ui-ppe.pearson.com/b0/ppe/fit/v1.4.2/fit.min.js",
    commonIesClientId: "oX5Vm6SEEeSi5QBEAU0081tx20UHE469",
  },
  prod: {
    cloudflicks: "https://spp.pearson.com/mf/sppPlayerMfRemoteEntry.js",
    TDX: "https://cdn.acs.pearsonprd.tech/tdx-fit-provider/prd/remoteEntryNoBundle.js",
    WSOL: "https://wsng-mf.pearson.com/uiNgMfRemoteEntry.js",
    fitjsUrl: "https://learningapps-ui.pearson.com/b0/fit/v1.4.2/fit.min.js",
    commonIesClientId: "hQ8ZsSZoIjyl8chQLgo7dB7fAlGIFvVG",
  },
};

/** SPP Video / Audio plugin UUIDs (from bronte-plugin-* GitLab PLUGIN_ID). */
export const SPP_PLUGIN_ID = {
  VIDEO: "b00a9ec1-db09-11f0-8faa-23369f995d14",
  AUDIO: "c178ff51-3426-11f1-b4b5-ebfb97fca625",
} as const;

// Plugin ID to component type mapping
export const ID: Record<string, string> = {
  [SPP_PLUGIN_ID.VIDEO]: COMPONENT.SPP, // SPP Video Player
  [SPP_PLUGIN_ID.AUDIO]: COMPONENT.SPP, // SPP Audio Player
  "65355f10-53d8-11eb-a967-25c6238ae566": COMPONENT.MMI,
  "c844e980-5f60-11ea-a852-e1cd1b741a1d": COMPONENT.IFRAME, // iFrame
  "a3a69e20-3844-11ec-b441-93434cb45c9f": COMPONENT.PAGEBREAK, // Page Break
  "cb421f41-9f12-11ee-a41c-e359e88b9ab0": COMPONENT.GLOSSARY, //Glossary
  "c84bca90-c583-11eb-bf96-1fcef4e59d02": COMPONENT.TDX, // TDX
  "afe4eef0-2717-11ec-901e-99005061ea5c": COMPONENT.CODE, // Code Block
  "265c7bd1-16f1-11f0-beb7-3f5872d1f706": COMPONENT.SINGLESPA, // Bronte plugin Single-SPA
  "bc7ced70-53d7-11eb-a967-25c6238ae567": COMPONENT.ETEXT_MCQ, // eText MCQ
  "1c25a4c0-13db-11f0-b563-75b59c9cb2d2": COMPONENT.JOURNAL, // Bronte Plugin Journal Learning Tool
  "cede8d90-68c1-11ea-af65-af7754595c61": COMPONENT.SPACER, // Spacer
  // Add other plugin ID mappings as needed
};
